import { useEffect, useState } from 'react';
import type { Catalogue } from '../types/landmark';
import type { Feature, MultiPolygon, Polygon } from 'geojson';

export type Zone = Feature<MultiPolygon | Polygon, { id: number; name: string }>;

interface CatalogueDataState {
  status: 'loading' | 'ready' | 'error';
  catalogue: Catalogue | null;
  zone: Zone | null;
  error: string | null;
}

/**
 * Fetch catalogue.json + zone.json on app boot. Both are served as static
 * assets from `public/data/` — no longer bundled into the main JS chunk
 * (Phase 5a perf win, ~350 KB gzipped saved on initial paint).
 *
 * Returns a status enum so the app can render a loading state on slow
 * networks without ever rendering an empty map.
 */
export function useCatalogueData(): CatalogueDataState {
  const [state, setState] = useState<CatalogueDataState>({
    status: 'loading',
    catalogue: null,
    zone: null,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;
    void Promise.all([
      fetch('/data/catalogue.json').then((r) => {
        if (!r.ok) throw new Error(`catalogue ${r.status}`);
        return r.json() as Promise<Catalogue>;
      }),
      fetch('/data/zone.json').then((r) => {
        if (!r.ok) throw new Error(`zone ${r.status}`);
        return r.json() as Promise<Zone>;
      }),
    ])
      .then(([catalogue, zone]) => {
        if (cancelled) return;
        setState({ status: 'ready', catalogue, zone, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'unknown';
        setState({ status: 'error', catalogue: null, zone: null, error: message });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
